// EVA-296: display formatting for the Valuation Summary tab and the floating
// Value Conclusion panel. Pure ESM so spec/javascript can probe it — the
// component owns markup only.
//
// Conventions follow the Exit Price Assumptions tab: en-GB grouping, whole
// currency amounts, two-decimal percentages, an em dash for "unavailable"
// (nil from the server — never rendered as zero), deductions in brackets.

export const EM_DASH = '—'

const WHOLE = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 })
const TWO_DP = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

// Exported for the Value Bridge tab, which reuses this module's display rules
// (EVA-303). A private copy there would be the third in the codebase.
export function toNumber(value) {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function formatWhole(value) {
  const parsed = toNumber(value)
  return parsed === null ? EM_DASH : WHOLE.format(parsed)
}

// A deduction renders as an absolute value in brackets when non-zero; a valid
// zero stays a visible plain zero.
export function formatDeduction(value) {
  const parsed = toNumber(value)
  if (parsed === null) return EM_DASH
  if (parsed === 0) return WHOLE.format(0)
  return `(${WHOLE.format(Math.abs(parsed))})`
}

export function formatPercent(value) {
  const parsed = toNumber(value)
  return parsed === null ? EM_DASH : `${TWO_DP.format(parsed)}%`
}

export function formatYears(value) {
  const parsed = toNumber(value)
  return parsed === null ? EM_DASH : TWO_DP.format(parsed)
}

// Sensitivity movement arrives as a RAW ratio (scenario / base − 1); positive
// movements carry an explicit +, and a valid 0 renders as 0.00%.
export function formatMovement(value) {
  const parsed = toNumber(value)
  if (parsed === null) return EM_DASH
  const percent = parsed * 100
  const text = `${TWO_DP.format(Math.abs(percent))}%`
  if (percent > 0) return `+${text}`
  if (percent < 0) return `-${text}`
  return `0.00%`
}

// The dynamic NOI label (the prototype hardcoded "Y11 NOI"; its own developer
// note brands that a defect). Returns an i18n key plus interpolation vars;
// the caller resolves it through the translations prop.
//
//   TWELVE_MONTHS_AFTER_SALE, aligned window  -> noi_label.year  {year}
//   TWELVE_MONTHS_AFTER_SALE, rolling window  -> noi_label.range {start, end}
//   YEAR_OF_SALE                              -> noi_label.year_of_sale
//   MANUAL                                    -> noi_label.entered
export function noiLabel({ method, selectedNoiYear, periodStart, periodEnd }) {
  if (method === 'MANUAL') return { key: 'noi_label.entered', vars: {} }
  if (method === 'YEAR_OF_SALE') return { key: 'noi_label.year_of_sale', vars: {} }
  if (selectedNoiYear !== null && selectedNoiYear !== undefined) {
    return { key: 'noi_label.year', vars: { year: selectedNoiYear } }
  }
  return {
    key: 'noi_label.range',
    vars: { start: formatMonthYear(periodStart), end: formatMonthYear(periodEnd) }
  }
}

const MONTH_YEAR = new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric' })

export function formatMonthYear(isoDate) {
  if (!isoDate) return EM_DASH
  const date = new Date(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return EM_DASH
  return MONTH_YEAR.format(date)
}

export function formatIsoDate(isoDate) {
  if (!isoDate) return EM_DASH
  const date = new Date(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return EM_DASH
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium' }).format(date)
}

// The "(6.20% + fixed)" fragments beside Less: Purchasers' Costs. Fragments
// carrying words come back as i18n keys ({ key, vars }) for the component to
// resolve — no user-visible string is hardcoded here; pre-formatted numbers
// come back as { text }. Null when there is nothing to say — the row then
// shows its bare label, mirroring the monetary owner exactly.
export function purchasersCostsHelperParts({ ratePercent, hasFixed }) {
  const parts = []
  const rate = toNumber(ratePercent)
  if (rate !== null && rate > 0) parts.push({ text: `${TWO_DP.format(rate)}%` })
  if (hasFixed) parts.push({ key: 'cost_helper.fixed', vars: {} })
  return parts.length > 0 ? parts : null
}

// The "(5.00% AGSP + 1.00% NSP + fixed)" fragments beside Less: Sales Costs.
export function salesCostsHelperParts({ agspRatePercent, nspRatePercent, hasFixed }) {
  const parts = []
  const agsp = toNumber(agspRatePercent)
  const nsp = toNumber(nspRatePercent)
  if (agsp !== null && agsp > 0) {
    parts.push({ key: 'cost_helper.agsp', vars: { rate: `${TWO_DP.format(agsp)}%` } })
  }
  if (nsp !== null && nsp > 0) {
    parts.push({ key: 'cost_helper.nsp', vars: { rate: `${TWO_DP.format(nsp)}%` } })
  }
  if (hasFixed) parts.push({ key: 'cost_helper.fixed', vars: {} })
  return parts.length > 0 ? parts : null
}
